import {
  ACTIVITY_FILTER_LABELS,
  ACTIVITY_FILTERS,
  type ActivityFilter,
  type ContactListItem,
  type ContactSort,
  DEAL_SOURCE_LABELS,
  formatPhone,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, Merge, Phone, Plus, Search, Trash2 } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { ClientEquipment, NewTicketButton } from '@/components/client-equipment';
import { Comments } from '@/components/comments';
import { ActivitiesPanel } from '@/components/crm-activities';
import { ChannelMark, ContactPicker, CrmNav, EmptyPanel, Pager } from '@/components/crm-bits';
import { ChatPanel } from '@/components/crm-chat';
import { CompanyFormDialog, ContactFormDialog, DealFormDialog } from '@/components/crm-forms';
import { Dialog } from '@/components/dialog';
import {
  type ColumnDef,
  ColumnsMenu,
  type ColumnsState,
  cleanParams,
  ExportButton,
  PresetChips,
  SavedFilters,
  SortHeader,
  type SortState,
  useColumns,
} from '@/components/list-tools';
import { Avatar, Button, ErrorBox, Input, PageHeader, Segmented, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { companiesQuery, companyQuery, contactQuery, contactsQuery, dealsQuery } from '@/lib/crm';
import { formatMoment } from '@/lib/format';
import { DealTable, NextActivityCell } from './crm-deals';

export interface ContactsSearch {
  q?: string;
  mine?: boolean;
  activity?: ActivityFilter;
  sort?: ContactSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

function useDebouncedSearch(initial: string | undefined, apply: (v: string | undefined) => void) {
  const [q, setQ] = useState(initial ?? '');
  const t = useRef<number | undefined>(undefined);
  return [
    q,
    (v: string) => {
      setQ(v);
      window.clearTimeout(t.current);
      t.current = window.setTimeout(() => apply(v.trim() || undefined), 250);
    },
  ] as const;
}

function SearchBox({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <div className="relative w-full max-w-[320px]">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
        aria-hidden
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="pl-9"
      />
    </div>
  );
}

type ContactColumn = 'company' | 'phone' | 'email' | 'city' | 'deals' | 'activity' | 'owner';
const CONTACT_COLUMNS: ColumnDef<ContactColumn>[] = [
  { key: 'company', label: 'Компания', defaultHidden: true },
  { key: 'phone', label: 'Телефон' },
  { key: 'email', label: 'E-mail', defaultHidden: true },
  { key: 'city', label: 'Город', defaultHidden: true },
  { key: 'deals', label: 'Сделки в работе' },
  { key: 'activity', label: 'Ближайшее дело' },
  { key: 'owner', label: 'Ответственный' },
];

export function ContactTable({
  items,
  sort,
  onSort,
  cols,
}: {
  items: ContactListItem[];
  sort?: SortState<ContactSort>;
  onSort?: (s: SortState<ContactSort>) => void;
  cols?: ColumnsState<ContactColumn>;
}) {
  const show = (k: ContactColumn) =>
    cols ? cols.show(k) : !CONTACT_COLUMNS.find((c) => c.key === k)?.defaultHidden;
  const head = (label: string, key: ContactSort) =>
    sort && onSort ? (
      <SortHeader label={label} sortKey={key} state={sort} onSort={onSort} />
    ) : (
      <th scope="col" className="px-4 py-2.5 font-medium">
        {label}
      </th>
    );
  return (
    <div className="panel relative overflow-x-auto">
      <table className="w-full min-w-[900px] text-left">
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            {head('Клиент', 'name')}
            {show('company') && head('Компания', 'company')}
            {show('phone') && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Телефон
              </th>
            )}
            {show('email') && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                E-mail
              </th>
            )}
            {show('city') && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Город
              </th>
            )}
            {show('deals') && head('Сделки в работе', 'deals')}
            {show('activity') && head('Ближайшее дело', 'activity')}
            {show('owner') && head('Ответственный', 'owner')}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((c) => (
            <tr key={c.id} className="hover:bg-paper/70">
              <td className="px-4 py-2.5">
                <span className="flex items-center gap-2">
                  <Link to="/crm/contacts/$id" params={{ id: c.id }} className="font-medium hover:text-link">
                    {c.fullName}
                  </Link>
                  {c.channels.map((ch) => (
                    <ChannelMark key={ch} channel={ch} />
                  ))}
                </span>
                <span className="block text-[12.5px] text-steel">
                  {[c.position, !show('company') && c.company?.name, !show('city') && c.city]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </td>
              {show('company') && (
                <td className="px-4 py-2.5 text-ink-soft">
                  {c.company?.name ?? <span className="text-steel">—</span>}
                </td>
              )}
              {show('phone') && (
                <td className="px-4 py-2.5 tabular-nums">
                  {c.phones[0] ? formatPhone(c.phones[0]) : <span className="text-steel">—</span>}
                </td>
              )}
              {show('email') && (
                <td className="px-4 py-2.5 text-ink-soft">
                  {c.emails[0] ?? <span className="text-steel">—</span>}
                </td>
              )}
              {show('city') && (
                <td className="px-4 py-2.5 text-ink-soft">
                  {c.city ?? <span className="text-steel">—</span>}
                </td>
              )}
              {show('deals') && (
                <td className="px-4 py-2.5 tabular-nums">
                  {c.openDeals || <span className="text-steel">—</span>}
                </td>
              )}
              {show('activity') && (
                <td className="px-4 py-2.5">
                  <NextActivityCell next={c.nextActivity} overdue={c.hasOverdueActivity} />
                </td>
              )}
              {show('owner') && (
                <td className="px-4 py-2.5 text-ink-soft">
                  {c.owner?.fullName ?? <span className="text-steel">общий</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ContactsPage() {
  const search = useSearch({ strict: false }) as ContactsSearch;
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const setSearch = (patch: Partial<ContactsSearch>) =>
    navigate({ to: '/crm/contacts', search: { ...search, page: undefined, ...patch }, replace: true });
  const [q, setQ] = useDebouncedSearch(search.q, (v) => setSearch({ q: v }));
  const cols = useColumns('contacts', CONTACT_COLUMNS);
  const filter = {
    q: search.q,
    mine: search.mine,
    activity: search.activity,
    sort: search.sort,
    dir: search.dir,
  };
  const list = useQuery(contactsQuery({ ...filter, page: search.page ?? 1 }));
  const current = cleanParams(filter);

  return (
    <div>
      <PageHeader
        title="CRM"
        description={list.data ? `Клиентов: ${list.data.total}` : undefined}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Клиент
          </Button>
        }
      />
      <CrmNav />
      {creating && <ContactFormDialog onClose={() => setCreating(false)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented
          label="Чьи клиенты"
          value={search.mine ? 'mine' : 'all'}
          onChange={(v) => setSearch({ mine: v === 'mine' || undefined })}
          options={[
            { value: 'all', label: 'Все' },
            { value: 'mine', label: 'Мои' },
          ]}
        />
        <SearchBox
          value={q}
          onChange={setQ}
          placeholder="Имя, телефон, e-mail, компания"
          label="Поиск клиентов"
        />
        <SavedFilters
          page="contacts"
          current={current}
          onApply={(p) => navigate({ to: '/crm/contacts', search: p as ContactsSearch, replace: true })}
        />
        <ColumnsMenu state={cols} />
        <ExportButton path="/crm/contacts/export.xlsx" query={filter} />
      </div>
      <div className="mb-4">
        <PresetChips
          label="Дела по клиентам"
          value={search.activity}
          onChange={(activity) => setSearch({ activity })}
          options={ACTIVITY_FILTERS.map((v) => ({ value: v, label: ACTIVITY_FILTER_LABELS[v] }))}
        />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title={search.q ? 'Никого не нашлось' : 'Клиентов пока нет'}>
          Клиенты появляются вручную или сами — когда пишут боту в Telegram или MAX.
        </EmptyPanel>
      ) : (
        <>
          <ContactTable
            items={list.data.items}
            sort={{ sort: search.sort, dir: search.dir }}
            onSort={(st) => setSearch({ sort: st.sort, dir: st.sort ? st.dir : undefined })}
            cols={cols}
          />
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => setSearch({ page: p })}
          />
        </>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12.5px] text-steel">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

export function ContactPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(contactQuery(id));
  const deals = useQuery(dealsQuery({ contactId: id, state: 'all' }));
  const [editing, setEditing] = useState(false);
  const [newDeal, setNewDeal] = useState(false);
  const [merging, setMerging] = useState(false);
  const [activeChat, setActiveChat] = useState(0);

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const c = q.data;
  const chat = c.chats[Math.min(activeChat, c.chats.length - 1)];

  return (
    <div className="max-w-[1240px]">
      <Link
        to="/crm/contacts"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Все клиенты
      </Link>
      <header className="mb-6 flex flex-wrap items-start gap-3">
        <Avatar name={c.fullName} size={48} />
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{c.fullName}</h1>
          <p className="mt-0.5 text-steel">
            {[c.position, c.company?.name, c.city].filter(Boolean).join(' · ') || 'Клиент'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setNewDeal(true)}>
            <Plus className="size-4" aria-hidden />
            Сделка
          </Button>
          <NewTicketButton
            label="Заявка на сервис"
            client={{
              kind: 'contact',
              id: c.id,
              label: c.company ? `${c.fullName} · ${c.company.name}` : c.fullName,
              companyId: c.company?.id ?? null,
            }}
          />
          {c.permissions.canEdit && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Изменить
            </Button>
          )}
        </div>
      </header>
      {editing && <ContactFormDialog contact={c} onClose={() => setEditing(false)} />}
      {newDeal && (
        <DealFormDialog
          defaultContact={{ id: c.id, label: c.fullName }}
          defaultCompany={c.company ? { id: c.company.id, label: c.company.name } : undefined}
          onClose={() => setNewDeal(false)}
        />
      )}
      {merging && <MergeDialog contactId={c.id} name={c.fullName} onClose={() => setMerging(false)} />}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          {chat && (
            <div>
              {c.chats.length > 1 && (
                <div className="mb-2 flex gap-1.5">
                  {c.chats.map((ch, i) => (
                    <button
                      key={ch.id}
                      type="button"
                      aria-pressed={i === activeChat}
                      onClick={() => setActiveChat(i)}
                      className={
                        i === activeChat
                          ? 'h-7 rounded bg-graphite px-2.5 text-[13px] text-white'
                          : 'h-7 rounded border border-line-strong px-2.5 text-[13px]'
                      }
                    >
                      {ch.channel === 'telegram' ? 'Telegram' : 'MAX'}
                    </button>
                  ))}
                </div>
              )}
              <ChatPanel chat={chat} className="h-[460px]" />
            </div>
          )}
          <section>
            <h2 className="mb-2 font-semibold">Сделки</h2>
            {deals.data?.items.length ? (
              <DealTable items={deals.data.items} compact />
            ) : (
              <p className="panel px-4 py-3 text-[13.5px] text-steel">Сделок пока нет.</p>
            )}
          </section>
          <ClientEquipment contactId={c.id} />
          <ActivitiesPanel contactId={c.id} />
          <Comments entityType="contact" entityId={c.id} />
        </div>
        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              <Row label="Телефоны">
                {c.phones.length ? (
                  c.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p}`}
                      className="flex items-center gap-1.5 tabular-nums hover:text-link"
                    >
                      <Phone className="size-3.5 text-steel" aria-hidden />
                      {formatPhone(p)}
                    </a>
                  ))
                ) : (
                  <span className="text-steel">не указаны</span>
                )}
              </Row>
              {c.emails.length > 0 && (
                <Row label="E-mail">
                  {c.emails.map((e) => (
                    <a key={e} href={`mailto:${e}`} className="block text-link hover:underline">
                      {e}
                    </a>
                  ))}
                </Row>
              )}
              {c.chats.length > 0 && (
                <Row label="Мессенджеры">
                  <span className="flex flex-wrap gap-2">
                    {c.chats.map((ch) => (
                      <span key={ch.id} className="inline-flex items-center gap-1.5">
                        <ChannelMark channel={ch.channel} />
                        {ch.username ? `@${ch.username}` : ch.title}
                      </span>
                    ))}
                  </span>
                </Row>
              )}
              {c.company && (
                <Row label="Компания">
                  <Link to="/crm/companies/$id" params={{ id: c.company.id }} className="link">
                    {c.company.name}
                  </Link>
                </Row>
              )}
              <Row label="Ответственный">
                {c.owner?.fullName ?? <span className="text-steel">общий клиент</span>}
              </Row>
              {c.source && <Row label="Откуда">{DEAL_SOURCE_LABELS[c.source]}</Row>}
              {c.notes && (
                <Row label="Заметки">
                  <span className="whitespace-pre-wrap">{c.notes}</span>
                </Row>
              )}
              <Row label="Добавлен">
                {formatMoment(c.createdAt)}
                {c.createdBy && <span className="text-steel"> · {c.createdBy.fullName}</span>}
              </Row>
            </dl>
          </section>
          {c.permissions.canEdit && (
            <div className="flex flex-col items-start gap-2">
              <button
                type="button"
                onClick={() => setMerging(true)}
                className="inline-flex items-center gap-1.5 text-[13px] text-steel hover:text-ink"
              >
                <Merge className="size-3.5" aria-hidden />
                Это дубль — объединить с другим клиентом
              </button>
              <DeleteContact id={c.id} />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function MergeDialog({ contactId, name, onClose }: { contactId: string; name: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [target, setTarget] = useState<{ id: string; label: string } | null>(null);
  const merge = useMutation({
    mutationFn: () =>
      api<{ id: string }>(`/crm/contacts/${contactId}/merge`, {
        method: 'POST',
        body: { targetId: target?.id },
      }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['crm'] });
      onClose();
      await navigate({ to: '/crm/contacts/$id', params: { id: res.id } });
    },
  });
  return (
    <Dialog title="Объединить с другим клиентом" onClose={onClose} width={520}>
      <div className="flex flex-col gap-4 px-5 py-5">
        <p className="text-[13.5px] text-ink-soft">
          Сделки, переписка, дела, комментарии и телефоны «{name}» перейдут к выбранному клиенту, а эта
          карточка удалится.
        </p>
        <ContactPicker
          value={target}
          onChange={(c) => setTarget(c && c.id !== contactId ? { id: c.id, label: c.fullName } : null)}
        />
        {merge.error && <ErrorBox error={merge.error} />}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!target} loading={merge.isPending} onClick={() => merge.mutate()}>
            Объединить
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function DeleteContact({ id }: { id: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/crm/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['crm'] });
      await navigate({ to: '/crm/contacts' });
    },
  });
  if (!confirm)
    return (
      <button
        type="button"
        onClick={() => setConfirm(true)}
        className="inline-flex items-center gap-1.5 text-[13px] text-steel hover:text-danger"
      >
        <Trash2 className="size-3.5" aria-hidden />
        Удалить клиента
      </button>
    );
  return (
    <div className="panel flex w-full flex-col gap-2 px-4 py-3">
      <p className="text-[13.5px]">Удалить клиента? Удалить можно только без сделок.</p>
      {del.error && <p className="text-[13px] text-danger">{del.error.message}</p>}
      <div className="flex gap-2">
        <Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()}>
          Удалить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

// ---------- Компании ----------

export function CompaniesPage() {
  const search = useSearch({ strict: false }) as { q?: string; page?: number };
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const setSearch = (patch: { q?: string; page?: number }) =>
    navigate({ to: '/crm/companies', search: { ...search, page: undefined, ...patch }, replace: true });
  const [q, setQ] = useDebouncedSearch(search.q, (v) => setSearch({ q: v }));
  const list = useQuery(companiesQuery({ q: search.q, page: search.page ?? 1 }));
  return (
    <div>
      <PageHeader
        title="CRM"
        description="Компании необязательны: сделки ведутся с контактами, компания — для реквизитов и группировки."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Компания
          </Button>
        }
      />
      <CrmNav />
      {creating && <CompanyFormDialog onClose={() => setCreating(false)} />}
      <div className="mb-4">
        <SearchBox value={q} onChange={setQ} placeholder="Название, ИНН, город" label="Поиск компаний" />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title="Компаний не найдено" />
      ) : (
        <>
          <div className="panel overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line text-[13px] text-steel">
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Компания
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    ИНН
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Контактов
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Сделки в работе
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-paper/70">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/crm/companies/$id"
                        params={{ id: c.id }}
                        className="font-medium hover:text-link"
                      >
                        {c.name}
                      </Link>
                      {c.city && <span className="block text-[12.5px] text-steel">{c.city}</span>}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">
                      {c.inn ?? <span className="text-steel">—</span>}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{c.contactCount}</td>
                    <td className="px-4 py-2.5 tabular-nums">
                      {c.openDeals || <span className="text-steel">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => setSearch({ page: p })}
          />
        </>
      )}
    </div>
  );
}

export function CompanyPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(companyQuery(id));
  const deals = useQuery(dealsQuery({ companyId: id, state: 'all' }));
  const [editing, setEditing] = useState(false);
  const [newContact, setNewContact] = useState(false);
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const c = q.data;
  return (
    <div className="max-w-[1240px]">
      <Link
        to="/crm/companies"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Все компании
      </Link>
      <header className="mb-6 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{c.name}</h1>
          <p className="mt-0.5 text-steel">
            {[c.inn && `ИНН ${c.inn}`, c.kpp && `КПП ${c.kpp}`, c.city].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <NewTicketButton
            label="Заявка на сервис"
            client={{ kind: 'company', id: c.id, label: c.name, companyId: c.id }}
          />
          <Button variant="secondary" onClick={() => setNewContact(true)}>
            <Plus className="size-4" aria-hidden />
            Контакт
          </Button>
          {c.permissions.canEdit && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Изменить
            </Button>
          )}
        </div>
      </header>
      {editing && <CompanyFormDialog company={c} onClose={() => setEditing(false)} />}
      {newContact && (
        <ContactFormDialog
          defaultCompany={{ id: c.id, label: c.name }}
          onClose={() => setNewContact(false)}
        />
      )}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          <section>
            <h2 className="mb-2 font-semibold">Контакты</h2>
            {c.contacts.length ? (
              <ContactTable items={c.contacts} />
            ) : (
              <p className="panel px-4 py-3 text-[13.5px] text-steel">Контактов нет.</p>
            )}
          </section>
          <section>
            <h2 className="mb-2 font-semibold">Сделки</h2>
            {deals.data?.items.length ? (
              <DealTable items={deals.data.items} compact />
            ) : (
              <p className="panel px-4 py-3 text-[13.5px] text-steel">Сделок нет.</p>
            )}
          </section>
          <ClientEquipment companyId={c.id} />
          <Comments entityType="company" entityId={c.id} />
        </div>
        <aside className="panel px-5 py-4">
          <dl className="flex flex-col gap-3.5">
            {c.address && <Row label="Адрес">{c.address}</Row>}
            {c.phones.length > 0 && (
              <Row label="Телефоны">
                {c.phones.map((p) => (
                  <a key={p} href={`tel:${p}`} className="block tabular-nums hover:text-link">
                    {formatPhone(p)}
                  </a>
                ))}
              </Row>
            )}
            {c.emails.length > 0 && <Row label="E-mail">{c.emails.join(', ')}</Row>}
            {c.website && <Row label="Сайт">{c.website}</Row>}
            <Row label="Ответственный">
              {c.owner?.fullName ?? <span className="text-steel">не назначен</span>}
            </Row>
            {c.notes && (
              <Row label="Заметки">
                <span className="whitespace-pre-wrap">{c.notes}</span>
              </Row>
            )}
          </dl>
        </aside>
      </div>
    </div>
  );
}
